/* Adapted from the supplied prototype's mapfx.js; rendering only, no prototype auth/storage/actions. */
import { mapPartGlyph } from '../../services/mapPartSymbols'
import { CITY_ZONES, cityZoneKey } from '../../services/mapCityScene'
import { incidentMarkerOffsets } from '../../services/mapRuntimeGeometry'
const escapeHtml = value => String(value == null ? '' : value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))
const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback
const safeColor = value => /^#[0-9a-f]{3,8}$/i.test(String(value)) ? value : '#dbeafe'

export function createPrototypeMapRenderer (template) {
  const esc = escapeHtml
  const U = { esc, find: (items, key, value) => items.find(item => item[key] === value), num: number }
  const customMap = Boolean(template.mapEditorSchema)
  const illustrated = !customMap || template.sceneSettings?.style === 'CITY'
  const referenceAppearance = customMap && illustrated && template.sceneSettings?.matchPrototype === true
  const zoneColors = Object.fromEntries(CITY_ZONES.map(zone => [zone.name, zone.color]))
  const zoneSource = customMap ? [...new Set((template.cells || []).map(c => illustrated ? cityZoneKey(c) : c.zoneKey || c.zone))].map(key => (template.zones || []).find(z => z.key === key) || CITY_ZONES.find(z => z.key === key) || { key, name: key, color: zoneColors[key] || '#dcebf9' }) : (template.zones || [])
  const zones = zoneSource.map(zone => ({ ...zone, color: safeColor(zone.color) }))
  const C = {
    W: number(template.width, 960), H: number(template.height, 640),
    CELL: number(template.unitWidth, 80), COLS: number(template.cols, 12), ROWS: number(template.rows, 8),
    cells: (template.cells || []).map(cell => ({
      id: String(cell.id), r: number(cell.row), c: number(cell.col), zone: illustrated ? cityZoneKey(cell) : cell.zoneKey || cell.zone,
      pop: number(cell.population), eco: number(cell.economy), risk: number(cell.risk), geometry: cell.geometry
    })),
    zones,
    refGrids: (template.gridSchemes || []).map(grid => ({ ...grid, cells: grid.cellIds || grid.cells || [] })),
    parts: template.parts || [],
    partPoints: (template.partPoints || []).map(point => ({ ...point, x: number(point.x), y: number(point.y) })),
    zoneColor: key => (zones.find(zone => zone.key === key) || {}).color || '#dbeafe',
    zoneName: key => (zones.find(zone => zone.key === key) || {}).name || key
  }
  var W = C.W, H = C.H, CELL = C.CELL, COLS = C.COLS, ROWS = C.ROWS;
  var FOOT = 46, VH = H + FOOT;

  /* ---------- 基础工具 ---------- */
  function r1(i) { var x = Math.sin(i * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); }
  function R(v) { return Math.round(v * 10) / 10; }
  function N(v) { return Math.round(v); }

  var HEAT_POP = [[238, 245, 255], [147, 197, 253], [59, 130, 246], [29, 78, 216], [23, 37, 84]];
  var HEAT_ECO = [[255, 251, 235], [253, 230, 138], [250, 204, 21], [217, 119, 6], [124, 45, 18]];
  function heatColor(t, stops) {
    t = Math.max(0, Math.min(1, t));
    var n = stops.length - 1, p = t * n, i = Math.min(n - 1, Math.floor(p)), f = p - i;
    var a = stops[i], b = stops[i + 1];
    return 'rgb(' + Math.round(a[0] + (b[0] - a[0]) * f) + ',' + Math.round(a[1] + (b[1] - a[1]) * f) + ',' + Math.round(a[2] + (b[2] - a[2]) * f) + ')';
  }
  function riskFill(r) { return r >= 3 ? '#f8b4b4' : (r === 2 ? '#fde08a' : '#b7f0c8'); }
  var GRID_COLOR = { A: '#1a56db', B: '#0e7490', C: '#15803d', D: '#b45309', E: '#6d28d9', F: '#b91c1c' };
  function gridColor(k) { return GRID_COLOR[k] || '#64748b'; }

  var CELL_MAP = {};
  C.cells.forEach(function (c) { CELL_MAP[c.id] = c; });
  function cellAt(r, c) { return CELL_MAP['C' + r + '_' + c]; }
  function cellRing(c) {
    var geo = c.geometry || {};
    if (String(geo.type).toUpperCase() === 'POLYGON') return ((geo.coordinates || [])[0] || []).map(p => [number(p[0]), number(p[1])]);
    var x = number(geo.x, c.c * CELL), y = number(geo.y, c.r * number(template.unitHeight, CELL)), w = number(geo.width, CELL), h = number(geo.height, number(template.unitHeight, CELL));
    return [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
  }
  function customShape(c, attributes, title) { return '<polygon points="' + cellRing(c).map(p => p.join(',')).join(' ') + '" ' + attributes + '><title>' + esc(title || '') + '</title></polygon>'; }
  function standardCell(c) {
    var ring=cellRing(c);
    return ring.length===4&&ring.every((p,i)=>Math.abs(p[0]-(c.c*CELL+(i===1||i===2?CELL:0)))<.000001&&Math.abs(p[1]-(c.r*CELL+(i>=2?CELL:0)))<.000001);
  }
  function configuredObjects(L) {
    return (template.objects || []).map(o => {
      var type = String(o.type || ''), water = type === 'WATER';
      if (water ? L.water === false : o.type === 'LANDMARK' ? !L.label : !L.bld) return '';
      var x = number(o.x), y = number(o.y), width = number(o.width, 60), height = number(o.height, 40);
      var shape = o.geometry && String(o.geometry.type).toUpperCase() === 'POLYGON' ? customShape({ geometry: o.geometry }, 'fill="' + (water ? '#74b8e8' : '#dcebf9') + '" stroke="#94a3b8"') : '<rect x="' + x + '" y="' + y + '" width="' + width + '" height="' + height + '" rx="4" fill="' + (water ? '#74b8e8' : type === 'PARK' ? '#b9f0d1' : '#fff0b3') + '" stroke="#94a3b8"/>';
      return shape + (L.label ? '<text x="' + (x + width / 2) + '" y="' + (y + height / 2) + '" text-anchor="middle" fill="#243b53" font-size="11">' + esc(o.name) + '</text>' : '');
    }).join('');
  }

  /* ---------- defs：渐变 / 图案 / 滤镜 ---------- */
  function defs() {
    var d = '<defs>';
    d += '<linearGradient id="gWater" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="#8fc0ee"/><stop offset="1" stop-color="#4d8fd6"/></linearGradient>';
    d += '<linearGradient id="gPark" x1="0" y1="0" x2="1" y2="1">' +
      '<stop offset="0" stop-color="#dcfce7"/><stop offset="1" stop-color="#a7f3c4"/></linearGradient>';
    d += '<linearGradient id="gFoot" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="#f8fafc"/><stop offset="1" stop-color="#eef2f7"/></linearGradient>';
    /* 住宅窗格 */
    d += '<pattern id="pWin" width="7" height="6.5" patternUnits="userSpaceOnUse">' +
      '<rect width="7" height="6.5" fill="#fbfcfe"/>' +
      '<rect x="1" y="1.4" width="2.1" height="2" fill="#c6d2e0"/>' +
      '<rect x="4.1" y="1.4" width="2.1" height="2" fill="#c6d2e0"/></pattern>';
    /* 玻璃幕墙 */
    d += '<pattern id="pGlass" width="5" height="5" patternUnits="userSpaceOnUse">' +
      '<rect width="5" height="5" fill="#dbeafe"/>' +
      '<rect x="0" y="0" width="4.3" height="4.3" fill="#bcd8f5"/>' +
      '<rect x="0" y="0" width="4.3" height="1.4" fill="#e8f2fd"/></pattern>';
    /* 厂房屋顶（人字锯齿） */
    d += '<pattern id="pInd" width="11" height="9" patternUnits="userSpaceOnUse">' +
      '<rect width="11" height="9" fill="#eaeff5"/>' +
      '<path d="M0 8.2 L5.5 1.6 L11 8.2" fill="none" stroke="#c4cedb" stroke-width="1"/></pattern>';
    /* 铺装 */
    d += '<pattern id="pPave" width="6" height="6" patternUnits="userSpaceOnUse">' +
      '<rect width="6" height="6" fill="#eef1f5"/><rect x="0" y="0" width="5.4" height="5.4" fill="none" stroke="#dde3ea" stroke-width=".6"/></pattern>';
    /* 铁轨 */
    d += '<pattern id="pRail" width="9" height="7" patternUnits="userSpaceOnUse">' +
      '<rect width="9" height="7" fill="#d8dde4"/>' +
      '<rect x="0" y="1.5" width="9" height="1" fill="#8a94a3"/>' +
      '<rect x="0" y="4.5" width="9" height="1" fill="#8a94a3"/></pattern>';
    /* 建筑投影 */
    d += '<filter id="fBld" x="-30%" y="-30%" width="170%" height="170%">' +
      '<feDropShadow dx="1.2" dy="1.6" stdDeviation="1.1" flood-color="#334155" flood-opacity="0.22"/></filter>';
    d += '<filter id="fSoft" x="-20%" y="-20%" width="140%" height="140%">' +
      '<feDropShadow dx="0" dy="1" stdDeviation="1.4" flood-color="#0f172a" flood-opacity="0.18"/></filter>';
    d += '</defs>';
    return d;
  }

  /* ---------- 小品图元 ---------- */
  function tree(x, y, r) {
    return '<circle cx="' + R(x) + '" cy="' + R(y) + '" r="' + R(r) + '" fill="#4ade80" opacity=".85"/>' +
      '<circle cx="' + R(x - r * 0.28) + '" cy="' + R(y - r * 0.28) + '" r="' + R(r * 0.55) + '" fill="#a7f3c4"/>';
  }
  function rect(x, y, w, h, fill, stroke, sw) {
    return '<rect x="' + R(x) + '" y="' + R(y) + '" width="' + R(Math.max(0, w)) + '" height="' + R(Math.max(0, h)) + '" fill="' + fill + '"' +
      (stroke ? ' stroke="' + stroke + '" stroke-width="' + (sw || 0.7) + '"' : '') + '/>';
  }

  /* ---------- 街区建筑：按功能分区生成形态 ---------- */
  function cellBox(r, c) {
    var m = 10;
    return { x: c * CELL + m, y: r * CELL + m, w: CELL - m * 2, h: CELL - m * 2 };
  }
  function buildings(r, c, z, showWater = true) {
    var b = cellBox(r, c), k = r * 31.7 + c * 7.3 + 2.1;
    var g = '';
    if (r === ROWS - 1 && (!customMap || template.sceneSettings?.riverbank)) { /* 滨江绿带 */
      g += '<rect x="' + (c * CELL) + '" y="' + (r * CELL + 8) + '" width="' + CELL + '" height="26" fill="url(#gPark)" opacity=".55"/>';
      g += '<path d="M' + (c * CELL) + ' ' + (r * CELL + 22) + ' h' + CELL + '" stroke="#e7f6ec" stroke-width="2.4" fill="none"/>';
      for (var q = 0; q < 3; q++) g += tree(c * CELL + 12 + q * 20 + r1(k + q) * 6, r * CELL + 18 + r1(k + q * 3) * 8, 4 + r1(k + q * 5) * 2);
      return g;
    }
    if (z === 'res') {
      var rows = 3, gap = 5, bh = (b.h - gap * (rows - 1) - 8) / rows;
      for (var i = 0; i < rows; i++) {
        var yy = b.y + i * (bh + gap);
        var ww = b.w * (0.54 + r1(k + i * 3.7) * 0.44);
        var xx = b.x + (b.w - ww) * (0.12 + r1(k + i * 9.3) * 0.72);
        g += rect(xx, yy, ww, bh, 'url(#pWin)', '#b9c6d6', 0.7);
        g += rect(xx, yy + bh - 1.5, ww, 1.5, '#ccd6e2');
      }
      g += rect(b.x, b.y + b.h - 7, b.w, 7, '#dcfce7');
      for (var t = 0; t < 2; t++) g += tree(b.x + 6 + t * (b.w - 12) + r1(k + t * 11) * 5, b.y + b.h - 3.5, 3.6 + r1(k + t) * 1.6);
    } else if (z === 'biz') {
      var tw = Math.min(b.w * 0.44, b.h * 0.66), th = b.h * 0.74;
      var tx = b.x + (b.w - tw) * (0.08 + r1(k) * 0.5), ty = b.y + 3;
      g += rect(b.x, b.y + b.h - 13, b.w, 13, 'url(#pGlass)', '#93b4d8', 0.7);
      g += rect(tx, ty, tw, th, 'url(#pGlass)', '#7ea6cf', 0.8);
      g += rect(tx + tw * 0.28, ty - 2.6, tw * 0.44, 2.6, '#b8c4d2');
      g += '<line x1="' + R(tx + tw / 2) + '" y1="' + R(ty - 2.6) + '" x2="' + R(tx + tw / 2) + '" y2="' + R(ty - 8) + '" stroke="#94a3b8" stroke-width=".8"/>';
      if (r1(k + 5) > 0.5) {
        var tw2 = tw * 0.7;
        g += rect(b.x + b.w - tw2 - 2, b.y + b.h * 0.16, tw2, b.h * 0.46, 'url(#pGlass)', '#7ea6cf', 0.7);
      }
      g += rect(b.x, b.y + b.h - 13, b.w * 0.28, 13, 'url(#pPave)', '#dbe2ea', 0.6);
    } else if (z === 'ind') {
      var fh = b.h * 0.48;
      g += rect(b.x, b.y, b.w * 0.76, fh, 'url(#pInd)', '#c3ccd8', 0.7);
      g += rect(b.x + b.w * 0.82, b.y + 3, 4, fh * 0.72, '#c8d2de');
      g += rect(b.x + b.w * 0.82, b.y, 6, 3, '#94a3b8');
      g += rect(b.x, b.y + fh + 5, b.w * 0.62, b.h - fh - 6, 'url(#pPave)', '#d3dae3', 0.6);
      for (var q2 = 0; q2 < 3; q2++) {
        var bw = 7 + r1(k + q2 * 4) * 5;
        g += rect(b.x + 3 + q2 * (bw + 3), b.y + fh + 8, bw, 5, r1(k + q2) > 0.5 ? '#93a3b8' : '#b45309', '#7c8b9e', 0.5);
      }
    } else if (z === 'edu') {
      g += rect(b.x, b.y, b.w * 0.34, b.h * 0.7, 'url(#pWin)', '#b9c6d6', 0.7);
      g += rect(b.x, b.y, b.w * 0.68, b.h * 0.3, 'url(#pWin)', '#b9c6d6', 0.7);
      var ex = b.x + b.w * 0.72, ey = b.y + b.h * 0.58;
      g += '<ellipse cx="' + R(ex) + '" cy="' + R(ey) + '" rx="' + R(b.w * 0.26) + '" ry="' + R(b.h * 0.28) + '" fill="#dcfce7" stroke="#86efac" stroke-width="1.4"/>';
      g += '<ellipse cx="' + R(ex) + '" cy="' + R(ey) + '" rx="' + R(b.w * 0.18) + '" ry="' + R(b.h * 0.19) + '" fill="none" stroke="#fb923c" stroke-width="1.1" stroke-dasharray="3 2"/>';
      g += tree(b.x + b.w * 0.42, b.y + b.h * 0.82, 3.4);
    } else if (z === 'pub') {
      g += rect(b.x + 1, b.y, b.w - 2, b.h * 0.56, 'url(#pWin)', '#a9b8ca', 0.8);
      for (var p2 = 1; p2 < 6; p2++) {
        g += '<line x1="' + R(b.x + 1 + (b.w - 2) * p2 / 6) + '" y1="' + R(b.y + 3) + '" x2="' + R(b.x + 1 + (b.w - 2) * p2 / 6) + '" y2="' + R(b.y + b.h * 0.56 - 2) + '" stroke="#c8d4e2" stroke-width=".9"/>';
      }
      g += rect(b.x + 4, b.y + b.h * 0.56, b.w - 8, 4, '#e2e8f0');
      g += rect(b.x, b.y + b.h * 0.62, b.w, b.h * 0.34, 'url(#pPave)', '#dbe2ea', 0.6);
      g += '<line x1="' + R(b.x + b.w / 2) + '" y1="' + R(b.y - 4) + '" x2="' + R(b.x + b.w / 2) + '" y2="' + R(b.y) + '" stroke="#94a3b8" stroke-width=".9"/>';
      g += '<path d="M' + R(b.x + b.w / 2) + ' ' + R(b.y - 11) + ' l0 7 l9 -3 l-9 -3" fill="#dc2626"/>';
    } else if (z === 'grn') {
      if (showWater) g += '<ellipse cx="' + R(b.x + b.w * 0.42) + '" cy="' + R(b.y + b.h * 0.42) + '" rx="' + R(b.w * 0.3) + '" ry="' + R(b.h * 0.26) + '" fill="url(#gWater)"/>';
      g += '<path d="M' + R(b.x) + ' ' + R(b.y + b.h * 0.86) + ' Q' + R(b.x + b.w * 0.5) + ' ' + R(b.y + b.h * 0.6) + ' ' + R(b.x + b.w) + ' ' + R(b.y + b.h * 0.8) + '" stroke="#f5f9f5" stroke-width="2.6" fill="none"/>';
      for (var t2 = 0; t2 < 5; t2++) {
        g += tree(b.x + 6 + r1(k + t2 * 7) * (b.w - 14), b.y + 6 + r1(k + t2 * 13) * (b.h - 14), 4 + r1(k + t2 * 3) * 2.4);
      }
    } else if (z === 'trf') {
      g += rect(b.x, b.y + b.h * 0.28, b.w, b.h * 0.3, 'url(#pGlass)', '#93b4d8', 0.7);
      g += rect(b.x + 3, b.y + b.h * 0.62, b.w - 6, b.h * 0.12, '#e2e8f0', '#cbd5e1', 0.6);
      g += rect(b.x, b.y + b.h * 0.8, b.w, b.h * 0.2, 'url(#pRail)', '#a8b2c0', 0.6);
      g += '<line x1="' + R(b.x) + '" y1="' + R(b.y + b.h * 0.9) + '" x2="' + R(b.x + b.w) + '" y2="' + R(b.y + b.h * 0.9) + '" stroke="#64748b" stroke-width=".9"/>';
      for (var v = 0; v < 3; v++) {
        g += rect(b.x + 4 + v * (b.w - 14) / 2.4, b.y + 4, 9, 5, r1(k + v) > 0.5 ? '#60a5fa' : '#f59e0b', '#8a94a3', 0.5);
      }
    }
    return g;
  }

  /* ---------- 路网 ---------- */
  var ROADS = (template.roads || []).map(road => ({ ...road, x1: number(road.x1), x2: number(road.x2), y1: number(road.y1), y2: number(road.y2), w: number(road.w, 10) }));
  var BRIDGES = (template.bridges || []).map(bridge => ({ ...bridge, x1: number(bridge.x1), x2: number(bridge.x2), y1: number(bridge.y1), y2: number(bridge.y2), w: number(bridge.w, 22) }));
  function bridges() {
    return BRIDGES.map(function (bridge, index) {
      var dx=bridge.x2-bridge.x1,dy=bridge.y2-bridge.y1,length=Math.hypot(dx,dy),w=bridge.w;
      if (!length || w<=0) return '';
      var g='<g data-scene-bridge="'+esc(bridge.id||index)+'" data-scene-bridge-kind="manual"><title>'+esc(bridge.name)+'</title>';
      if (dx===0) {
        var x=bridge.x1,y=Math.min(bridge.y1,bridge.y2),bottom=Math.max(bridge.y1,bridge.y2);
        g+='<rect x="'+(x-w/2)+'" y="'+y+'" width="'+w+'" height="'+length+'" fill="#f1f5f9" stroke="#b8c4d2" stroke-width="1"/>';
        g+='<line x1="'+(x-w/2)+'" y1="'+y+'" x2="'+(x+w/2)+'" y2="'+y+'" stroke="#cbd5e1" stroke-width="2"/>';
        g+='<line x1="'+(x-w/2)+'" y1="'+bottom+'" x2="'+(x+w/2)+'" y2="'+bottom+'" stroke="#cbd5e1" stroke-width="2"/>';
        if(bridge.cls==='art')g+='<line x1="'+x+'" y1="'+y+'" x2="'+x+'" y2="'+bottom+'" stroke="#f6c344" stroke-width="1" stroke-dasharray="4 4"/>';
      } else {
        // 归一化方向，让反向绘制得到相同桥面和标线。
        var reverse=dx<0,ax=reverse?bridge.x2:bridge.x1,ay=reverse?bridge.y2:bridge.y1,angle=Math.atan2(reverse?-dy:dy,Math.abs(dx))*180/Math.PI;
        g+='<g transform="translate('+ax+' '+ay+') rotate('+angle+')"><rect x="0" y="'+(-w/2)+'" width="'+length+'" height="'+w+'" fill="#f1f5f9" stroke="#b8c4d2" stroke-width="1"/>';
        g+='<line x1="0" y1="'+(-w/2)+'" x2="0" y2="'+w/2+'" stroke="#cbd5e1" stroke-width="2"/>';
        g+='<line x1="'+length+'" y1="'+(-w/2)+'" x2="'+length+'" y2="'+w/2+'" stroke="#cbd5e1" stroke-width="2"/>';
        if(bridge.cls==='art')g+='<line x1="0" y1="0" x2="'+length+'" y2="0" stroke="#f6c344" stroke-width="1" stroke-dasharray="4 4"/>';
        g+='</g>';
      }
      return g+'</g>';
    }).join('');
  }
  function roads() {
    var g = '';
    /* 支路：街区格网细线 */
    if (!customMap || referenceAppearance) {
    for (var i = 1; i < COLS; i++) g += '<line x1="' + (i * CELL) + '" y1="0" x2="' + (i * CELL) + '" y2="' + H + '" stroke="#e2e8f0" stroke-width="1"/>';
    for (var j = 1; j < ROWS; j++) g += '<line x1="0" y1="' + (j * CELL) + '" x2="' + W + '" y2="' + (j * CELL) + '" stroke="#e2e8f0" stroke-width="1"/>';
    }
    ROADS.forEach(function (d, index) {
      g += '<g data-scene-road="' + esc(d.id || index) + '">';
      var art = d.cls === 'art', w = d.w;
      g += '<line x1="' + d.x1 + '" y1="' + d.y1 + '" x2="' + d.x2 + '" y2="' + d.y2 + '" stroke="#c3ccd8" stroke-width="' + (w + 2.4) + '" stroke-linecap="round"/>';
      g += '<line x1="' + d.x1 + '" y1="' + d.y1 + '" x2="' + d.x2 + '" y2="' + d.y2 + '" stroke="#fbfcfe" stroke-width="' + w + '" stroke-linecap="round"/>';
      if (art) {
        g += '<line x1="' + d.x1 + '" y1="' + d.y1 + '" x2="' + d.x2 + '" y2="' + d.y2 + '" stroke="#f6c344" stroke-width="1.1" stroke-dasharray="9 7"/>';
        var dx = d.y1 === d.y2 ? 0 : w / 2 - 1, dy = d.x1 === d.x2 ? 0 : w / 2 - 1;
        g += '<line x1="' + (d.x1 + dx) + '" y1="' + (d.y1 + dy) + '" x2="' + (d.x2 + dx) + '" y2="' + (d.y2 + dy) + '" stroke="#e6ebf2" stroke-width=".8" stroke-dasharray="5 5"/>';
        g += '<line x1="' + (d.x1 - dx) + '" y1="' + (d.y1 - dy) + '" x2="' + (d.x2 - dx) + '" y2="' + (d.y2 - dy) + '" stroke="#e6ebf2" stroke-width=".8" stroke-dasharray="5 5"/>';
      }
      /* 路名 */
      var dx = d.x2 - d.x1, dy = d.y2 - d.y1, length = Math.hypot(dx, dy), horiz = Math.abs(dx) >= Math.abs(dy);
      var reverse = horiz ? dx < 0 : dy < 0, ax = reverse ? d.x2 : d.x1, ay = reverse ? d.y2 : d.y1;
      var vx = reverse ? -dx : dx, vy = reverse ? -dy : dy, ratio = length ? Math.min(horiz ? 26 : 78, length / 2) / length : 0;
      var lx = R(ax + vx * ratio + (horiz ? 0 : 8)), ly = R(ay + vy * ratio - (horiz ? 6 : 0));
      var angle = Math.atan2(vy, vx) * 180 / Math.PI;
      if (Math.abs(angle) < .5) angle = 0;
      else if (Math.abs(angle - 90) < .5) angle = -90;
      else if (angle > 90) angle -= 180;
      angle = R(angle);
      g += '<text data-scene-road-label="' + esc(d.id || index) + '" x="' + lx + '" y="' + ly + '" font-size="10.5" font-weight="600" fill="#64748b" ' +
        (angle ? 'transform="rotate(' + angle + ' ' + lx + ' ' + ly + ')"' : '') +
        ' style="paint-order:stroke;stroke:#fff;stroke-width:3px;stroke-linejoin:round">' + esc(d.name) + '</text>';
      if (d.bridge && !referenceAppearance) g += '<line x1="' + d.x1 + '" y1="' + d.y1 + '" x2="' + d.x2 + '" y2="' + d.y2 + '" stroke="#94a3b8" stroke-width="' + (w + 3) + '" opacity=".2"/>';
      g += '</g>';
    });
    return g;
  }

  /* ---------- 水体（南江）与桥梁 ---------- */
  var RIVER = 'M-20 612 C200 640 340 602 520 614 C700 626 840 600 980 608';
  function water() {
    var g = '';
    g += '<path d="' + RIVER + '" stroke="#cfe0f3" stroke-width="30" fill="none" stroke-linecap="round" opacity=".75"/>';
    g += '<path d="' + RIVER + '" stroke="url(#gWater)" stroke-width="24" fill="none" stroke-linecap="round"/>';
    g += '<path d="M-20 606 C200 634 340 596 520 608 C700 620 840 594 980 602" stroke="#dbeeff" stroke-width="1.6" fill="none" opacity=".65"/>';
    g += '<path d="M-20 620 C200 648 340 610 520 622 C700 634 840 608 980 616" stroke="#7fb3e6" stroke-width="1.2" fill="none" opacity=".55"/>';
    /* 桥梁 */
    [320, 640, 800].forEach(function (bx) {
      g += '<rect x="' + (bx - 11) + '" y="586" width="22" height="52" fill="#f1f5f9" stroke="#b8c4d2" stroke-width="1"/>';
      g += '<line x1="' + (bx - 11) + '" y1="586" x2="' + (bx + 11) + '" y2="586" stroke="#cbd5e1" stroke-width="2"/>';
      g += '<line x1="' + (bx - 11) + '" y1="638" x2="' + (bx + 11) + '" y2="638" stroke="#cbd5e1" stroke-width="2"/>';
      g += '<line x1="' + bx + '" y1="586" x2="' + bx + '" y2="638" stroke="#f6c344" stroke-width="1" stroke-dasharray="4 4"/>';
    });
    g += '<text x="150" y="632" font-size="11" font-weight="700" fill="#1d4ed8" style="paint-order:stroke;stroke:#fff;stroke-width:3.2px;stroke-linejoin:round">南　江</text>';
    return g;
  }

  /* ---------- 分区标注 / 地标 ---------- */
  var LANDMARKS = (template.landmarks || []).map(mark => ({ ...mark, n: mark.name || mark.n, i: mark.icon || mark.i || '🏢', x: number(mark.x), y: number(mark.y) }));
  function labels() {
    var g = '';
    LANDMARKS.forEach(function (m, index) {
      g += '<g data-scene-landmark="' + esc(m.id || index) + '">';
      g += '<circle cx="' + m.x + '" cy="' + m.y + '" r="9.5" fill="#fff" stroke="#1a56db" stroke-width="1.4" opacity=".95"/>';
      g += '<text x="' + m.x + '" y="' + (m.y + 3.6) + '" font-size="9.5" text-anchor="middle">' + esc(m.i) + '</text>';
      g += '<text x="' + m.x + '" y="' + (m.y + 21) + '" font-size="10.5" font-weight="700" fill="#1e293b" text-anchor="middle" style="paint-order:stroke;stroke:#fff;stroke-width:3.4px;stroke-linejoin:round">' + esc(m.n) + '</text></g>';
    });
    return g;
  }

  /* ---------- 静态底图（可缓存） ---------- */
  var STATIC_CACHE = {};
  function referenceRiver(water, showBridges) {
    var x=number(water.x),y=number(water.y),w=number(water.width),h=number(water.height),sx=w/960,sy=h/54;
    var g='<g transform="translate('+x+' '+y+') scale('+sx+' '+sy+') translate(0 -586)">';
    g+='<path d="'+RIVER+'" stroke="#cfe0f3" stroke-width="30" fill="none" stroke-linecap="round" opacity=".75"/>';
    g+='<path d="'+RIVER+'" stroke="url(#gWater)" stroke-width="24" fill="none" stroke-linecap="round"/>';
    g+='<path d="M-20 606 C200 634 340 596 520 608 C700 620 840 594 980 602" stroke="#dbeeff" stroke-width="1.6" fill="none" opacity=".65"/>';
    g+='<path d="M-20 620 C200 648 340 610 520 622 C700 634 840 608 980 616" stroke="#7fb3e6" stroke-width="1.2" fill="none" opacity=".55"/>';
    if (showBridges && sx > 0 && sy > 0) ROADS.filter(road=>road.bridge&&road.x1===road.x2&&road.x1>=x&&road.x1<=x+w&&Math.min(road.y1,road.y2)<=y&&Math.max(road.y1,road.y2)>=y+h*52/54).forEach(road=>{
      var bx=(road.x1-x)/sx;
      g+='<g data-scene-bridge="'+esc(road.id)+'"><rect x="'+(bx-11)+'" y="586" width="22" height="52" fill="#f1f5f9" stroke="#b8c4d2" stroke-width="1"/>';
      g+='<line x1="'+(bx-11)+'" y1="586" x2="'+(bx+11)+'" y2="586" stroke="#cbd5e1" stroke-width="2"/>';
      g+='<line x1="'+(bx-11)+'" y1="638" x2="'+(bx+11)+'" y2="638" stroke="#cbd5e1" stroke-width="2"/>';
      g+='<line x1="'+bx+'" y1="586" x2="'+bx+'" y2="638" stroke="#f6c344" stroke-width="1" stroke-dasharray="4 4"/></g>';
    });
    g+='<text x="150" y="632" font-size="11" font-weight="700" fill="#1d4ed8" style="paint-order:stroke;stroke:#fff;stroke-width:3.2px;stroke-linejoin:round">'+esc(water.name==='南江'?'南　江':water.name)+'</text></g>';
    return g;
  }
  function configuredWater(showBridges = true) {
    return (template.waterBodies || []).map((water, index) => {
      if(water.type==='RIVER')return '<g data-scene-water="'+esc(water.id||index)+'">'+referenceRiver(water,showBridges)+'</g>';
      var x=number(water.x), y=number(water.y), w=Math.max(0,number(water.width)), h=Math.max(0,number(water.height));
      var shape='';
      if (water.type === 'POND') shape='<ellipse cx="'+(x+w/2)+'" cy="'+(y+h/2)+'" rx="'+w/2+'" ry="'+h/2+'" fill="url(#gWater)" stroke="#7fb3e6" stroke-width="1.5"/>';
      else {
        var path='M'+x+' '+(y+h*.5)+' C'+(x+w*.22)+' '+(y+h*.85)+' '+(x+w*.36)+' '+(y+h*.15)+' '+(x+w*.52)+' '+(y+h*.5)+' S'+(x+w*.82)+' '+(y+h*.15)+' '+(x+w)+' '+(y+h*.5);
        shape='<path d="'+path+'" stroke="#cfe0f3" stroke-width="'+h*.65+'" fill="none"/><path d="'+path+'" stroke="url(#gWater)" stroke-width="'+h*.5+'" fill="none"/><path d="'+path+'" stroke="#dbeeff" stroke-width="1" fill="none" opacity=".7"/>';
      }
      return '<g data-scene-water="'+esc(water.id||index)+'">'+shape+'<text x="'+(x+w*.2)+'" y="'+(y+h*.65)+'" fill="#1d4ed8" font-size="11" font-weight="700" style="paint-order:stroke;stroke:#fff;stroke-width:2px">'+esc(water.name)+'</text></g>';
    }).join('');
  }
  function staticLayer(L) {
    var key = [L.bld,L.road,L.water!==false,L.label,illustrated].map(Boolean).join(':');
    if (STATIC_CACHE[key]) return STATIC_CACHE[key];
    var g = '';
    if (L.bld && illustrated) {
      C.cells.forEach(function (c, index) {
        var art=buildings(c.r,c.c,c.zone,L.water!==false);
        var regular=standardCell(c);
        if (customMap && !(referenceAppearance && regular)) {
          var points=cellRing(c),xs=points.map(p=>p[0]),ys=points.map(p=>p[1]);
          if(!points.length)return;
          var x=Math.min(...xs),y=Math.min(...ys),sx=(Math.max(...xs)-x)/CELL,sy=(Math.max(...ys)-y)/CELL;
          var clip='city-cell-'+index;
          g+='<defs><clipPath id="'+clip+'"><polygon points="'+points.map(p=>p.join(',')).join(' ')+'"/></clipPath></defs><g data-scene-building="'+esc(c.id)+'" clip-path="url(#'+clip+')"><g transform="translate('+x+' '+y+') scale('+sx+' '+sy+') translate('+(-c.c*CELL)+' '+(-c.r*CELL)+')" filter="url(#fBld)">'+art+'</g></g>';
        } else g += '<g data-scene-building="'+esc(c.id)+'" filter="url(#fBld)">' + art + '</g>';
      });
    }
    if(customMap){
      if(referenceAppearance){if(L.road&&ROADS.length)g+='<g>'+roads()+'</g>';if(L.water!==false)g+=configuredWater(L.road);}
      else{if(L.water!==false)g+=configuredWater(L.road);if(L.road && ROADS.length)g+='<g>'+roads()+'</g>';}
    }else{
      if(L.road)g+='<g>'+roads()+'</g>';
      if(L.water!==false)g+='<g>'+water()+'</g>';
    }
    if (L.road && BRIDGES.length) g += bridges();
    if (L.label) g += '<g>' + labels() + '</g>';
    STATIC_CACHE[key] = g;
    return g;
  }
  function sceneLayer(layers) { return staticLayer(layers) + (customMap ? configuredObjects(layers) : ''); }
  function renderScene(layers) {
    var L={bld:true,road:true,water:true,label:true,...layers};
    return defs()+'<defs><clipPath id="city-map-bounds"><rect width="'+W+'" height="'+H+'"/></clipPath></defs><g clip-path="url(#city-map-bounds)">'+sceneLayer(L)+'</g>';
  }

  /* ---------- 网格轮廓 ---------- */
  function outline(cells, color, w, dash, op) {
    if (customMap) {
      var edges = {};
      cells.forEach(function (id) { var c = CELL_MAP[id]; if (!c) return; var p = cellRing(c); if (p.length > 1 && p[0].join(',') === p[p.length - 1].join(',')) p = p.slice(0, -1); p.forEach(function (a, i) { var b = p[(i + 1) % p.length]; var key = [a.join(','), b.join(',')].sort().join('|'); edges[key] = edges[key] ? null : [a, b]; }); });
      return Object.values(edges).filter(Boolean).map(function (e) { return '<line x1="' + e[0][0] + '" y1="' + e[0][1] + '" x2="' + e[1][0] + '" y2="' + e[1][1] + '" stroke="' + safeColor(color) + '" stroke-width="' + number(w, 2) + '" stroke-dasharray="' + esc(dash) + '" opacity="' + esc(op) + '"/>'; }).join('');
    }
    var set = {};
    cells.forEach(function (id) { set[id] = 1; });
    var g = '', st = 'stroke="' + color + '" stroke-width="' + w + '" stroke-linecap="round"' + (dash ? ' stroke-dasharray="' + dash + '"' : '') + (op ? ' opacity="' + op + '"' : '');
    cells.forEach(function (id) {
      var c = CELL_MAP[id]; if (!c) return;
      var x = c.c * CELL, y = c.r * CELL;
      if (!set['C' + (c.r - 1) + '_' + c.c]) g += '<line x1="' + x + '" y1="' + y + '" x2="' + (x + CELL) + '" y2="' + y + '" ' + st + '/>';
      if (!set['C' + (c.r + 1) + '_' + c.c]) g += '<line x1="' + x + '" y1="' + (y + CELL) + '" x2="' + (x + CELL) + '" y2="' + (y + CELL) + '" ' + st + '/>';
      if (!set['C' + c.r + '_' + (c.c - 1)]) g += '<line x1="' + x + '" y1="' + y + '" x2="' + x + '" y2="' + (y + CELL) + '" ' + st + '/>';
      if (!set['C' + c.r + '_' + (c.c + 1)]) g += '<line x1="' + (x + CELL) + '" y1="' + y + '" x2="' + (x + CELL) + '" y2="' + (y + CELL) + '" ' + st + '/>';
    });
    return g;
  }
  function centroid(cells) {
    if (customMap) { var points = cells.flatMap(id => CELL_MAP[id] ? cellRing(CELL_MAP[id]) : []); return points.length ? { x: points.reduce((sum, p) => sum + p[0], 0) / points.length, y: points.reduce((sum, p) => sum + p[1], 0) / points.length, n: cells.length } : null; }
    var r = 0, c = 0, n = 0;
    cells.forEach(function (id) { var x = CELL_MAP[id]; if (x) { r += x.r; c += x.c; n++; } });
    if (!n) return null;
    return { x: (c / n + 0.5) * CELL, y: (r / n + 0.5) * CELL, n: n };
  }

  /* ---------- POI ---------- */
  function partPin(p, collected) {
    var col = collected ? '#16a34a' : '#f59e0b';
    var g = '<g class="pt" data-pt="' + esc(p.id) + '" style="cursor:pointer">';
    g += '<circle cx="' + p.x + '" cy="' + p.y + '" r="8" fill="#fff" stroke="' + col + '" stroke-width="2.2"/>';
    g += '<text x="' + p.x + '" y="' + (p.y + 3.4) + '" font-size="8.5" text-anchor="middle" fill="' + col + '" pointer-events="none">' + esc(mapPartGlyph(p.icon)) + '</text>';
    g += '<title>' + esc(p.name) + (collected ? '（已采集）' : '（点击查看）') + '</title></g>';
    return g;
  }
  function casePin(k, closed, idx) {
    var col = closed ? '#16a34a' : (k.level === '紧急' ? '#dc2626' : (k.level === '较急' ? '#ea580c' : '#ca8a04'));
    var g = '';
    if (!closed && k.level === '紧急') {
      g += '<circle cx="' + k.x + '" cy="' + (k.y - 16) + '" r="11" fill="' + col + '" opacity=".22">' +
        '<animate attributeName="r" values="9;18;9" dur="2s" repeatCount="indefinite"/>' +
        '<animate attributeName="opacity" values=".3;0;.3" dur="2s" repeatCount="indefinite"/></circle>';
    }
    g += '<path d="M' + k.x + ' ' + (k.y + 2) + ' c-6.2-8.4-8.8-12-8.8-16.6 a8.8 8.8 0 1 1 17.6 0 c0 4.6-2.6 8.2-8.8 16.6 z" fill="' + col + '" stroke="#fff" stroke-width="1.7"/>';
    g += '<circle cx="' + k.x + '" cy="' + (k.y - 16.4) + '" r="5.6" fill="#fff"/>';
    g += '<text x="' + k.x + '" y="' + (k.y - 12.8) + '" font-size="8" font-weight="700" fill="' + col + '" text-anchor="middle">' + idx + '</text>';
    g += '<title>' + esc(k.title) + ' · ' + esc(k.level) + '（' + (closed ? '已结案' : '待处置') + '）</title>';
    return g;
  }

  /* ---------- 图廓装饰 ---------- */
  function decorate(mode, footer = true) {
    var settings=template.sceneSettings || {};
    var g = '';
    /* 指北针 */
    if(settings.showCompass!==false){
    var nx = Math.max(18,W - 52), ny = Math.min(34,H*.1);
    g += '<circle cx="' + nx + '" cy="' + ny + '" r="15" fill="#fff" opacity=".92" stroke="#cbd5e1" stroke-width="1"/>';
    g += '<path d="M' + nx + ' ' + (ny - 11) + ' L' + (nx + 5.6) + ' ' + (ny + 8) + ' L' + nx + ' ' + (ny + 3.4) + ' L' + (nx - 5.6) + ' ' + (ny + 8) + ' Z" fill="#1a56db"/>';
    g += '<text data-scene-compass="1" x="' + nx + '" y="' + (ny - 13.5) + '" font-size="9" font-weight="700" fill="#1a56db" text-anchor="middle">N</text>';
    }
    /* 比例尺 */
    if(settings.showScale!==false){
    var length=Math.min(W*.4,Math.max(.001,number(settings.scaleUnits,120))), meters=length*number(settings.metersPerUnit,400/120);
    var sx = Math.max(10, W - length - 98), sy = Math.max(18,H - 30);
    g += '<rect x="' + (sx - 8) + '" y="' + (sy - 14) + '" width="'+(length+56)+'" height="24" rx="5" fill="#fff" opacity=".88" stroke="#dbe2ea"/>';
    g += '<line x1="' + sx + '" y1="' + sy + '" x2="' + (sx + length) + '" y2="' + sy + '" stroke="#334155" stroke-width="2"/>';
    g += '<line x1="' + sx + '" y1="' + (sy - 4) + '" x2="' + sx + '" y2="' + (sy + 4) + '" stroke="#334155" stroke-width="2"/>';
    g += '<line x1="' + (sx + length / 2) + '" y1="' + (sy - 3) + '" x2="' + (sx + length / 2) + '" y2="' + (sy + 3) + '" stroke="#334155" stroke-width="1.4"/>';
    g += '<line x1="' + (sx + length) + '" y1="' + (sy - 4) + '" x2="' + (sx + length) + '" y2="' + (sy + 4) + '" stroke="#334155" stroke-width="2"/>';
    g += '<text x="' + sx + '" y="' + (sy - 6) + '" font-size="9" fill="#334155">0</text>';
    g += '<text data-scene-scale="1" x="' + (sx + length - 8) + '" y="' + (sy - 6) + '" font-size="9" fill="#334155">' + R(meters) + ' m</text>';
    }
    /* 底部信息带 */
    if(footer){
    g += '<rect x="0" y="' + H + '" width="' + W + '" height="' + FOOT + '" fill="url(#gFoot)"/>';
    g += '<line x1="0" y1="' + H + '" x2="' + W + '" y2="' + H + '" stroke="#cbd5e1" stroke-width="1"/>';
    g += '<text x="16" y="' + (H + 19) + '" font-size="12" font-weight="700" fill="#0f172a">' + esc(template.name || '辖区实训底图') + '</text>';
    g += '<text x="16" y="' + (H + 35) + '" font-size="10.5" fill="#64748b">示意底图　坐标 LOCAL_SCREEN　底图单元 ' + COLS + '×' + ROWS + '（共 ' + C.cells.length + ' 个）　制图：智慧城市网络化管理平台</text>';
    g += '<text x="' + (W - 16) + '" y="' + (H + 27) + '" font-size="10.5" fill="#475569" text-anchor="end">当前图层：' + esc(modeName(mode)) + '</text>';
    }
    return g;
  }
  function modeName(m) {
    return { zone: '功能分区', pop: '人口密度热力', eco: '经济水平热力', risk: '公共安全风险', grid: '网格划分视图', base: '基础底图' }[m] || '功能分区';
  }

  /* ---------- 图例 ---------- */
  function legend(mode) {
    var h = '<div class="map-legend">';
    if (mode === 'zone') {
      C.zones.forEach(function (z) { h += '<span><i style="background:' + z.color + '"></i>' + esc(z.name) + '</span>'; });
    } else if (mode === 'pop' || mode === 'eco') {
      var st = mode === 'pop' ? HEAT_POP : HEAT_ECO;
      var grad = st.map(function (c, i) { return 'rgb(' + c.join(',') + ') ' + (i / (st.length - 1) * 100) + '%'; }).join(',');
      h += '<span class="heat-scale">低 <span class="bar" style="background:linear-gradient(90deg,' + grad + ')"></span> 高 · ' + (mode === 'pop' ? '人口密度' : '经济水平') + '热力图层</span>';
    } else if (mode === 'risk') {
      h += '<span><i style="background:#b7f0c8"></i>低风险</span><span><i style="background:#fde08a"></i>中风险</span><span><i style="background:#f8b4b4"></i>高风险</span>';
    } else if (mode === 'grid') {
      ['A', 'B', 'C', 'D', 'E', 'F'].forEach(function (k) { h += '<span><i style="background:' + gridColor(k) + '"></i>网格 ' + k + '</span>'; });
    } else {
      h += '<span>基础底图</span>';
    }
    h += '<span style="margin-left:auto;color:#64748b">🅿 部件点位　📍 案件点位（红=紧急/橙=较急/黄=一般/绿=已结案）</span>';
    h += '</div>';
    return h;
  }

  /* ---------- 主渲染 ---------- */
  var DEF_LAYERS = { bld: 1, road: 1, water: true, part: 1, cs: 1, ref: 0, label: 1 };
  function render(mode, s, opt) {
    opt = opt || {};
    var L = opt.layers || DEF_LAYERS;
    var cc = opt.caseObj || null;
    var g = '';
    g += defs();
    /* 底图 */
    g += '<rect x="0" y="0" width="' + W + '" height="' + H + '" fill="#eef2f7"/>';

    /* 1. 分区 / 热力 / 风险 / 网格 底色 */
    var gridOf = {};
    if (s && s.grids) {
      s.grids.forEach(function (gr) { (gr.cells || []).forEach(function (id) { gridOf[id] = gr.code; }); });
    }
    g += '<g>';
    C.cells.forEach(function (c) {
      var fill = '#f8fafc';
      if (mode === 'zone') fill = C.zoneColor(c.zone);
      else if (mode === 'pop') fill = heatColor(c.pop / 100, HEAT_POP);
      else if (mode === 'eco') fill = heatColor(c.eco / 100, HEAT_ECO);
      else if (mode === 'risk') fill = riskFill(c.risk);
      else if (mode === 'grid') fill = gridOf[c.id] ? gridColor(gridOf[c.id]) : '#eef2f7';
      var tip = '单元 ' + esc(c.id) + '｜' + C.zoneName(c.zone) + '｜人口密度 ' + c.pop + '｜经济水平 ' + c.eco + '｜风险 ' + (c.risk >= 3 ? '高' : c.risk === 2 ? '中' : '低') + (gridOf[c.id] ? '｜网格 ' + gridOf[c.id] : '');
      if (customMap && !(referenceAppearance && standardCell(c))) g += customShape(c, 'class="cell" data-cell="' + esc(c.id) + '" fill="' + fill + '" fill-opacity="'+(referenceAppearance?(mode==='grid'?(gridOf[c.id]?'.78':'.55'):(mode==='zone'?'.9':'.92')):'.85')+'"'+(referenceAppearance?' style="stroke:#94a3b8;stroke-width:.6"':' stroke="#b5cbe0"'), tip);
      else g += '<rect class="cell" data-cell="' + esc(c.id) + '" x="' + (c.c * CELL) + '" y="' + (c.r * CELL) + '" width="' + CELL + '" height="' + CELL + '" fill="' + fill + '" fill-opacity="' + (mode === 'grid' ? (gridOf[c.id] ? '.78' : '.55') : (mode === 'zone' ? '.9' : '.92')) + '"'+(referenceAppearance?' style="stroke:#94a3b8;stroke-width:.6"':'')+'><title>' + esc(tip) + '</title></rect>';
    });
    g += '</g>';

    /* 2. 静态层：建筑 / 路网 / 水体 / 标注（建筑在热力模式下淡化） */
    var st = sceneLayer(L);
    var op = (mode === 'pop' || mode === 'eco' || mode === 'risk') ? '.42' : (mode === 'grid' ? '.5' : '');
    g += '<g' + (op ? ' opacity="' + op + '"' : '') + ' style="pointer-events:none">' + st + '</g>';

    /* 3. 网格边界 + 网格代码 */
    if (mode === 'grid' && s && s.grids && s.grids.length) {
      g += '<g style="pointer-events:none">';
      s.grids.forEach(function (gr) {
        if (!gr.cells || !gr.cells.length) return;
        var col = gridColor(gr.code);
        g += outline(gr.cells, col, 3.2, '', '.95');
        var ct = centroid(gr.cells);
        if (ct) {
          g += '<circle cx="' + R(ct.x) + '" cy="' + R(ct.y) + '" r="20" fill="#fff" opacity=".82" stroke="' + col + '" stroke-width="2"/>';
          g += '<text x="' + R(ct.x) + '" y="' + R(ct.y + 7) + '" font-size="20" font-weight="800" fill="' + col + '" text-anchor="middle">' + esc(gr.code) + '</text>';
          g += '<text x="' + R(ct.x) + '" y="' + R(ct.y + 34) + '" font-size="11" font-weight="600" fill="#0f172a" text-anchor="middle" style="paint-order:stroke;stroke:#fff;stroke-width:3px;stroke-linejoin:round">' + esc(gr.name || '') + '（' + gr.cells.length + ' 单元）</text>';
        }
      });
      g += '</g>';
    }
    /* 4. 参考网格对照（虚线） */
    if (L.ref) {
      g += '<g style="pointer-events:none">';
      C.refGrids.forEach(function (gr) {
        g += outline(gr.cells, '#0f172a', 2, '7 5', '.55');
        var ct = centroid(gr.cells);
        if (ct) g += '<text x="' + R(ct.x) + '" y="' + R(ct.y - 14) + '" font-size="11" font-weight="700" fill="#0f172a" text-anchor="middle" opacity=".6" style="paint-order:stroke;stroke:#fff;stroke-width:3px">参考 ' + esc(gr.code) + '</text>';
      });
      g += '</g>';
    }

    /* 5. 部件点位 */
    if (L.part) {
      g += '<g>';
      C.partPoints.forEach(function (p) {
        var collected = s && s.space && s.space.some(function (x) { return x.partId === p.id; });
        var pk = U.find(C.parts, 'key', p.key);
        g += partPin({ id: p.id, x: p.x, y: p.y, name: p.name, icon: pk ? pk.icon : '●' }, collected);
      });
      g += '</g>';
    }

    /* 6. 案件点位 */
    if (L.cs && s && s.released && s.released.length && cc) {
      var markerOffsets = incidentMarkerOffsets(cc.cases);
      g += '<g class="map-case-pins">';
      s.released.forEach(function (id, i) {
        var k = U.find(cc.cases, 'id', id); if (!k) return;
        var offset = markerOffsets[cc.cases.indexOf(k)];
        g += '<g data-case="' + esc(k.id) + '" class="map-case-pin" role="button" tabindex="0" aria-label="' + esc(k.title) + '" transform="translate(' + offset[0] + ' ' + offset[1] + ')">' + casePin(k, !!(s.prog[id] && s.prog[id].closed), k.mapNumber || i + 1) + '</g>';
      });
      g += '</g>';
    }

    /* 7. 图廓 */
    g += '<g style="pointer-events:none">' + decorate(mode) + '</g>';
    g += '<rect x="0.5" y="0.5" width="' + (W - 1) + '" height="' + (VH - 1) + '" fill="none" stroke="#94a3b8" stroke-width="1"/>';

    var z = Math.max(1, Math.min(3, U.num(opt.zoom, 1) || 1));
    var vw = W / z, vh = VH / z, vx = (W - vw) / 2, vy = (VH - vh) / 2;
    var vb = R(vx) + ' ' + R(vy) + ' ' + R(vw) + ' ' + R(vh);
    return '<div class="map-box"><svg viewBox="' + vb + '" data-map="1" role="img" aria-label="辖区可视化底图" style="display:block;width:100%;height:auto;background:#fff">' + g + '</svg>' + legend(mode) + '</div>';
  }


  return { render, modeName, renderScene, renderDecor: mode => decorate(mode, false), zoneColor: cell => C.zoneColor(illustrated ? cityZoneKey(cell) : cell.zoneKey || cell.zone) }
}
